<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h2 class="mb-0">Constructor de Menús</h2>
          <p class="text-muted mb-0">Administra el menú, sus categorías, productos y modificadores desde una sola pantalla.</p>
        </b-col>
      </b-row>

      <!-- Menu selection bar -->
      <b-card class="mb-3">
        <b-row align-v="end">
          <b-col md="5">
            <b-form-group label="Menú:" class="mb-md-0">
              <b-form-select v-model="selectedMenuId" :options="menuOptions" @change="onMenuChange"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="7" class="text-md-right mt-2 mt-md-0">
            <b-button size="sm" variant="success" @click="openCreateMenu">
              <b-icon-plus></b-icon-plus> Nuevo menú
            </b-button>
            <b-button size="sm" variant="warning" :disabled="!selectedMenu" @click="openEditMenu">
              Editar menú
            </b-button>
            <b-button size="sm" variant="info" :disabled="!selectedMenuId" @click="viewSchedules">
              Horarios
            </b-button>
            <b-button size="sm" variant="primary" :disabled="!selectedMenuId || sending" @click="sendMenu">
              <b-spinner v-if="sending" small></b-spinner>
              Enviar a plataformas
            </b-button>
          </b-col>
        </b-row>
        <b-row v-if="selectedMenu" class="mt-2">
          <b-col>
            <b-badge :variant="selectedMenu.is_active ? 'success' : 'danger'">
              {{ selectedMenu.is_active ? 'Activo' : 'Inactivo' }}
            </b-badge>
            <span v-if="selectedMenu.description" class="text-muted ml-2">{{ selectedMenu.description }}</span>
          </b-col>
        </b-row>
      </b-card>

      <!-- Empty state -->
      <b-card v-if="!selectedMenuId" class="text-center py-5">
        <b-icon-card-list font-scale="3" variant="secondary"></b-icon-card-list>
        <h4 class="mt-3">Selecciona o crea un menú</h4>
        <p class="text-muted">Elige un menú de la lista para editar sus categorías, productos y modificadores, o crea uno nuevo.</p>
        <b-button variant="success" @click="openCreateMenu">
          <b-icon-plus></b-icon-plus> Crear menú
        </b-button>
      </b-card>

      <!-- Unified builder tabs -->
      <b-card v-else no-body>
        <b-tabs card v-model="activeTab">
          <b-tab title="Categorías">
            <platform-categories :embedded="true" :menu-id="selectedMenuId"></platform-categories>
          </b-tab>
          <b-tab title="Productos">
            <platform-items :embedded="true" :menu-id="selectedMenuId"></platform-items>
          </b-tab>
          <b-tab title="Modificadores">
            <platform-modifiers :embedded="true" :menu-id="selectedMenuId"></platform-modifiers>
          </b-tab>
          <b-tab title="Vista previa" lazy>
            <platform-menu-preview :menu-id="selectedMenuId"></platform-menu-preview>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-container>

    <!-- Create/Edit Menu Modal -->
    <b-modal
      id="modal-builder-menu"
      :title="isEditingMenu ? 'Editar Menu' : 'Crear Menu'"
      :ok-title="isEditingMenu ? 'Guardar cambios' : 'Crear menú'"
      cancel-title="Cancelar"
      ok-variant="success"
      :ok-disabled="!menuNameValid || savingMenu"
      :busy="savingMenu"
      @ok.prevent="submitMenu"
      @hidden="resetMenuForm">
      <b-form-group label="Nombre:" :state="menuNameValid" invalid-feedback="El nombre es requerido">
        <b-form-input
          ref="menuName"
          v-model="menuForm.name"
          :state="menuNameValid"
          placeholder="Ej. Menú Comida Rápida"
          @keyup.enter="submitMenu"
        ></b-form-input>
      </b-form-group>
      <b-form-group label="Descripción:">
        <b-form-textarea v-model="menuForm.description" rows="3" placeholder="Describe el propósito de este menú"></b-form-textarea>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="menuForm.is_active">Menu Activo</b-form-checkbox>
        <small class="text-muted d-block">Solo los menús activos se pueden enviar a las plataformas.</small>
      </b-form-group>
    </b-modal>

    <!-- Schedules Modal -->
    <b-modal id="modal-builder-schedules" title="Horarios del Menu" size="lg" @hidden="resetScheduleForm" ok-only ok-title="Cerrar">
      <h5 class="mb-3">Horarios para: {{ selectedMenu ? selectedMenu.name : '' }}</h5>
      <b-table
        small
        responsive
        striped
        hover
        :items="schedules"
        :fields="scheduleFields"
        :busy="loadingSchedules"
        show-empty
        empty-text="Sin horarios configurados.">
        <template #table-busy>
          <div class="text-center my-2"><b-spinner class="align-middle"></b-spinner></div>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="danger" @click="deleteSchedule(data.item.id)">Eliminar</b-button>
        </template>
      </b-table>
      <b-row class="mt-3">
        <b-col md="3">
          <b-form-group label="Día:">
            <b-form-select v-model="newSchedule.day_of_week" :options="daysOfWeek"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Hora inicio:">
            <b-form-input v-model="newSchedule.start_time" type="time"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Hora fin:">
            <b-form-input v-model="newSchedule.end_time" type="time"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end">
          <b-button variant="success" class="mb-3" @click="addSchedule">Agregar</b-button>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../Nav.vue";
import PlatformCategories from "./platform-categories.vue";
import PlatformItems from "./platform-items.vue";
import PlatformModifiers from "./platform-modifiers.vue";
import PlatformMenuPreview from "./platform-menu-preview.vue";

export default {
  name: "PlatformMenuBuilder",
  components: { Nav, PlatformCategories, PlatformItems, PlatformModifiers, PlatformMenuPreview },
  data() {
    return {
      menus: [],
      selectedMenuId: "",
      activeTab: 0,
      sending: false,
      // Menu create/edit
      isEditingMenu: false,
      savingMenu: false,
      menuForm: { id: "", name: "", description: "", is_active: true },
      // Schedules
      schedules: [],
      loadingSchedules: false,
      newSchedule: { day_of_week: "monday", start_time: "08:00", end_time: "22:00" },
      scheduleFields: [
        { key: "day_of_week", label: "Día", formatter: this.formatDay },
        { key: "start_time", label: "Hora Inicio" },
        { key: "end_time", label: "Hora Fin" },
        { key: "actions", label: "Acciones" }
      ],
      daysOfWeek: [
        { value: "monday", text: "Lunes" },
        { value: "tuesday", text: "Martes" },
        { value: "wednesday", text: "Miércoles" },
        { value: "thursday", text: "Jueves" },
        { value: "friday", text: "Viernes" },
        { value: "saturday", text: "Sábado" },
        { value: "sunday", text: "Domingo" }
      ]
    };
  },
  computed: {
    menuOptions() {
      return [
        { value: "", text: "Selecciona un menú", disabled: true },
        ...this.menus.map(m => ({ value: m.id, text: m.name + (m.is_active ? "" : " (inactivo)") }))
      ];
    },
    selectedMenu() {
      return this.menus.find(m => m.id === this.selectedMenuId) || null;
    },
    menuNameValid() {
      return !!(this.menuForm.name && this.menuForm.name.trim().length);
    }
  },
  mounted() {
    this.getMenus().then(() => {
      if (this.$route.params.menu_id) {
        this.selectedMenuId = this.$route.params.menu_id;
      }
    });
  },
  methods: {
    getMenus() {
      return axios
        .get("/platform/menus")
        .then(response => {
          this.menus = Array.isArray(response.data) ? response.data : [];
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener menús", "danger");
        });
    },
    onMenuChange() {
      // menu changed via selector; child components react to :menu-id prop
      this.activeTab = 0;
    },
    // ---- Menu CRUD ----
    openCreateMenu() {
      this.isEditingMenu = false;
      this.menuForm = { id: "", name: "", description: "", is_active: true };
      this.$bvModal.show("modal-builder-menu");
      this.$nextTick(() => this.$refs.menuName && this.$refs.menuName.focus());
    },
    openEditMenu() {
      if (!this.selectedMenu) return;
      this.isEditingMenu = true;
      this.menuForm = { ...this.selectedMenu };
      this.$bvModal.show("modal-builder-menu");
    },
    submitMenu() {
      if (!this.menuNameValid || this.savingMenu) return;
      const editing = this.isEditingMenu;
      const method = editing ? "PUT" : "POST";
      const url = editing ? `/platform/menu/${this.menuForm.id}` : "/platform/menu";
      const payload = { ...this.menuForm, name: this.menuForm.name.trim() };
      this.savingMenu = true;
      axios({ method, url, data: payload })
        .then(response => {
          this.makeToast("Éxito", `Menú ${editing ? "actualizado" : "creado"} exitosamente`, "success");
          this.$bvModal.hide("modal-builder-menu");
          return this.getMenus().then(() => {
            if (!editing && response.data && response.data.id) {
              this.selectedMenuId = response.data.id;
              this.activeTab = 0;
            }
          });
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || `Error al ${editing ? "actualizar" : "crear"} el menú`, "danger");
        })
        .finally(() => {
          this.savingMenu = false;
        });
    },
    resetMenuForm() {
      this.isEditingMenu = false;
      this.menuForm = { id: "", name: "", description: "", is_active: true };
    },
    // ---- Send ----
    sendMenu() {
      this.$bvModal
        .msgBoxConfirm("¿Enviar este menú a las plataformas asignadas?")
        .then(ok => {
          if (!ok) return;
          this.sending = true;
          axios
            .post(`/platform/menu/${this.selectedMenuId}/send`)
            .then(() => this.makeToast("Éxito", "Menú enviado", "success"))
            .catch(err => this.makeToast("Error", err.response?.data?.message || "Error al enviar el menú", "danger"))
            .finally(() => {
              this.sending = false;
            });
        });
    },
    // ---- Schedules ----
    viewSchedules() {
      this.getSchedules();
      this.$bvModal.show("modal-builder-schedules");
    },
    getSchedules() {
      this.loadingSchedules = true;
      axios
        .get(`/platform/menu/${this.selectedMenuId}/schedules`)
        .then(response => {
          this.schedules = Array.isArray(response.data) ? response.data : [];
        })
        .catch(err => this.makeToast("Error", err.response?.data?.message || "Error al obtener horarios", "danger"))
        .finally(() => {
          this.loadingSchedules = false;
        });
    },
    addSchedule() {
      if (!this.newSchedule.day_of_week || !this.newSchedule.start_time || !this.newSchedule.end_time) {
        this.makeToast("Error", "Todos los campos son requeridos", "danger");
        return;
      }
      axios
        .post(`/platform/menu/${this.selectedMenuId}/schedule`, this.newSchedule)
        .then(() => {
          this.makeToast("Éxito", "Horario agregado", "success");
          this.getSchedules();
          this.resetScheduleForm();
        })
        .catch(err => this.makeToast("Error", err.response?.data?.message || "Error al agregar horario", "danger"));
    },
    deleteSchedule(id) {
      axios
        .delete(`/platform/menu/schedule/${id}`)
        .then(() => {
          this.makeToast("Éxito", "Horario eliminado", "success");
          this.getSchedules();
        })
        .catch(err => this.makeToast("Error", err.response?.data?.message || "Error al eliminar horario", "danger"));
    },
    resetScheduleForm() {
      this.newSchedule = { day_of_week: "monday", start_time: "08:00", end_time: "22:00" };
    },
    formatDay(value) {
      const map = { monday: "Lunes", tuesday: "Martes", wednesday: "Miércoles", thursday: "Jueves", friday: "Viernes", saturday: "Sábado", sunday: "Domingo" };
      return map[value] || value;
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    }
  }
};
</script>
